import {useIsOverviewEnabled} from '../../hooks/use-feature-state'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {CommentDiscussionIcon} from '@primer/octicons-react'
import {Button, IconButton, Tooltip} from '@primer/react'
import {useRef} from 'react'
import {clsx} from 'clsx'

import {CounterSkeleton} from './CounterSkeleton'
import {CommentsSidePanel} from './CommentsSidePanel'
import styles from './PanelToggleButton.module.css'

import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {DockedPanel, useDockedPanelActions, useIsDockedPanelOpen} from '../../contexts/DockedPanelContext'
import {useUnresolvedConversationCount} from '../../stores/layout-selectors'
import type {PageLimits} from '../../page-data/payloads/files'

export interface OpenCommentsSidePanelButtonProps {
  className?: string
  buttonClasses?: string
  showMarkerCounts?: boolean
  size?: 'small' | 'medium'
  /**
   * Page limits forwarded to CommentsSidePanel (banner about thread limits).
   * Only meaningful when the button is rendered from PullRequestFilesToolbar
   * on the files-changed route and the Overview preview is not enabled.
   */
  pageLimits?: PageLimits
}

/**
 * Renders a button that opens the comments panel.
 *
 * Panel content rendering target depends on whether the Overview preview is enabled:
 * - **Enabled**: Panel content is rendered by `DockedSidePanelContent` directly
 *   (this button is just a trigger).
 * - **Not enabled**: Panel content is rendered as a child of this button so it inherits
 *   `PullRequestStoreProvider` scope needed by `ThreadPositioningWithStore`,
 *   then portaled into the docked-panel target via `createPortal` inside
 *   `CommentsSidePanel` itself.
 */
export function OpenCommentsPanelButton({
  className,
  buttonClasses,
  showMarkerCounts = true,
  size = 'small',
  pageLimits,
}: OpenCommentsSidePanelButtonProps) {
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.Comments)

  const togglePanelRef = useRef<HTMLButtonElement>(null)
  const actions = useDockedPanelActions()
  const isOpen = isDockedPanelOpen
  const buttonAriaLabel = isDockedPanelOpen ? 'Close comments panel' : 'Open comments panel'

  const showUpdatedHeader = useIsOverviewEnabled()
  const unresolvedConversationCount = useUnresolvedConversationCount()

  const isLoading = showMarkerCounts && unresolvedConversationCount === null
  const diffCommentCount = unresolvedConversationCount ?? 0

  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  // When the Overview preview is not enabled, render CommentsSidePanel as a child so it inherits
  // PullRequestStoreProvider scope (needed by ThreadPositioningWithStore via
  // useDiffEntries). The panel itself portals into DOCKED_SIDE_PANEL_CONTENT_ID
  // for visual placement. Only mount when open to avoid the panel's data work
  // when closed (mirrors how DockedSidePanelContent gates on `isCommentsOpen`).
  const renderPortaledPanel = !showUpdatedHeader && isOpen

  const handleTogglePanel = () => {
    sendPullRequestAnalyticsEvent('comments_side_panel.open', 'COMMENTS_SIDE_PANEL_BUTTON')
    actions.togglePanel('comments', {returnFocusTo: togglePanelRef.current})
  }

  return (
    <div className={clsx('d-flex flex-items-center', className)}>
      <ErrorBoundary
        boundaryName="OpenCommentsPanelButton"
        fallback={
          <IconButton
            aria-label="The comments panel cannot currently be opened."
            icon={CommentDiscussionIcon}
            size={size}
            variant="default"
          />
        }
      >
        {showMarkerCounts ? (
          <Tooltip ref={togglePanelRef} text={buttonAriaLabel} direction="sw" type="label">
            <Button
              className={clsx(styles.panelToggle, buttonClasses)}
              aria-expanded={isOpen}
              count={isLoading ? undefined : diffCommentCount}
              leadingVisual={CommentDiscussionIcon}
              trailingVisual={isLoading ? CounterSkeleton : undefined}
              size={size}
              variant="default"
              onClick={handleTogglePanel}
            />
          </Tooltip>
        ) : (
          <IconButton
            ref={togglePanelRef}
            className={clsx(styles.panelToggle, buttonClasses)}
            aria-label={buttonAriaLabel}
            aria-expanded={isOpen}
            icon={CommentDiscussionIcon}
            size={size}
            variant="default"
            onClick={handleTogglePanel}
          />
        )}
        {renderPortaledPanel && (
          <ErrorBoundary boundaryName="CommentsSidePanelPortaled" fallback={null}>
            <CommentsSidePanel pageLimits={pageLimits} />
          </ErrorBoundary>
        )}
      </ErrorBoundary>
    </div>
  )
}
